import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useSummarisePlanResultsMutation } from "src/store/api/generatedApi";
import { PlanId } from "../boundary/PlanId";
import { Period } from "hooks/periodIndex";
import { getDriMass } from "./utils";

interface BodyProps {
  inventoryAmount: number;
  consumptionAmount: number;
}
interface Props {
  inventoryAmount: number;
  planId: PlanId;
  period: Period;
  massScaleFactor: number;
}

const Body = ({ inventoryAmount, consumptionAmount }: BodyProps) => {
  const theme = useTheme();
  const { format } = useNumberSerialiser({ decimalPlaces: 0 });

  const backgroundColor =
    consumptionAmount > inventoryAmount
      ? theme.palette.error.main
      : theme.palette.common.white;
  const color =
    consumptionAmount > inventoryAmount
      ? theme.palette.common.white
      : theme.palette.common.black;

  return (
    <Typography
      variant="body1Mono"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor,
        color,
      }}
    >
      {format(consumptionAmount)}
    </Typography>
  );
};

export const DRITotalConsumptionCell = ({
  inventoryAmount,
  planId,
  period,
  massScaleFactor,
}: Props) => {
  const [, summarisePlanResultsMutationResult] =
    useSummarisePlanResultsMutation({
      fixedCacheKey: `${planId}-${period}`,
    });

  switch (summarisePlanResultsMutationResult.status) {
    case QueryStatus.rejected:
    case QueryStatus.uninitialized:
      return null;
    case QueryStatus.pending:
      return (
        <Box sx={{ width: "100%", justifyContent: "center", display: "flex" }}>
          <Skeleton width={25} />
        </Box>
      );
    case QueryStatus.fulfilled: {
      const consumptionAmount =
        summarisePlanResultsMutationResult.data.mixes.reduce(
          (total, result) => {
            const numberOfHeats = result.production.reduce(
              (sum, item) => sum + item.num_heats,
              0
            );
            return total + getDriMass(result.mix) * numberOfHeats;
          },
          0
        );
      return (
        <Body
          inventoryAmount={inventoryAmount * massScaleFactor}
          consumptionAmount={consumptionAmount * massScaleFactor}
        />
      );
    }
  }
};
